import { useFundsTableConfig } from "@funds/hooks/use-funds-table-config";
import { useFundsTableState } from "@funds/hooks/use-funds-table-state";
import { useFundFiltersMetadata } from "creso-core/hooks";
import { DistinctFilterMetadata } from "creso-core/types";
import { XIcon } from "lucide-react";
import { isEmpty } from "radash";
import { Fragment, useMemo } from "react";
import { Badge } from "web-design-system";
import { isViewApplied, removeViewFilters, SMART_VIEWS } from "./smart-views";

export function FundFilterChips() {
  const { filters, updateFilters } = useFundsTableState();

  const appliedFilters = Object.entries(filters).flatMap(([, f]) =>
    Array.isArray(f) ? f : f ? [f] : []
  );

  const { filterConfig } = useFundsTableConfig();

  const visibleFiltersIds = useMemo(
    () =>
      filterConfig
        .filter((fc) => fc.visible)
        .map((fc) => fc.fieldId)
        .filter(Boolean) as string[],
    [filterConfig]
  );

  const { data: filtersMetadata, isPending } =
    useFundFiltersMetadata(visibleFiltersIds);

  if (!appliedFilters?.length || isPending) return null;

  return (
    <div className="flex flex-row gap-2 flex-wrap">
      {SMART_VIEWS.filter((view) => isViewApplied(filters, view.filters)).map(
        (view) => (
          <Badge
            key={view.id}
            variant="outline"
            className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
          >
            {view.label}
            <XIcon
              onClick={() =>
                updateFilters(removeViewFilters(filters, view.filters))
              }
              className="size-3"
            />
          </Badge>
        )
      )}
      {filterConfig.flatMap((fc) => {
        if (fc.type === "MULTI") {
          const { filterKey, fieldId } = fc;
          const filterValues = filters[filterKey] as string[];
          if (isEmpty(filterValues)) return null;
          const allFilters =
            (
              filtersMetadata?.find(
                (md) => md.type === "distinct" && md.field === fieldId
              ) as DistinctFilterMetadata
            )?.values || [];

          const grouped = allFilters.reduce(
            (acc, v) => {
              const group = v.group || "DEFAULT_GROUP";
              acc[group] = acc[group] || [];
              acc[group].push(v);
              return acc;
            },
            {} as Record<string, typeof allFilters>
          );

          const isFlat =
            Object.keys(grouped).length === 1 &&
            Object.keys(grouped)[0] === "DEFAULT_GROUP";

          if (isFlat) {
            return (
              <Fragment key={fc.id}>
                {filterValues.map((fv) => {
                  const name = allFilters?.find(
                    (af) => af.id === fv || af.id.includes(fv)
                  )?.name as string;
                  const label = fc.formatter ? fc.formatter(name) : name;
                  return (
                    <Badge
                      key={fv}
                      variant="outline"
                      className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
                    >
                      {label}
                      <XIcon
                        onClick={() =>
                          updateFilters({
                            ...filters,
                            [filterKey]: filterValues.filter((v) => v !== fv),
                          })
                        }
                        className="size-3"
                      />
                    </Badge>
                  );
                })}
              </Fragment>
            );
          }
          return Object.entries(grouped).map(([group, children]) => {
            const childIds = children.map((c) => c.id);
            const selectedChildIds = childIds.filter((id) =>
              filterValues.includes(id)
            );
            if (selectedChildIds.length === 0) return null;

            if (selectedChildIds.length === childIds.length) {
              return (
                <Badge
                  key={group}
                  variant="outline"
                  className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
                >
                  {group}
                  <XIcon
                    onClick={() =>
                      updateFilters({
                        ...filters,
                        [filterKey]: filterValues.filter(
                          (v) => !childIds.includes(v)
                        ),
                      })
                    }
                    className="size-3"
                  />
                </Badge>
              );
            }
            return selectedChildIds.map((id) => {
              const child = children.find((c) => c.id === id);
              return (
                <Badge
                  key={id}
                  variant="outline"
                  className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
                >
                  {child?.name}
                  <XIcon
                    onClick={() =>
                      updateFilters({
                        ...filters,
                        [filterKey]: filterValues.filter((v) => v !== id),
                      })
                    }
                    className="size-3"
                  />
                </Badge>
              );
            });
          });
        }
        if (fc.type === "RANGE") {
          const { fieldId, filterKey, formatter } = fc;
          const { min, max } = filterKey;
          const { unit } = formatter;
          const minFilterValue = filters[min];
          const maxFilterValue = filters[max];
          if (minFilterValue && maxFilterValue) {
            const formattedMinFilterValue = formatter.in(
              minFilterValue as string
            );
            const formattedMaxFilterValue = formatter.in(
              maxFilterValue as string
            );
            return (
              <Badge
                key={fieldId}
                variant="outline"
                className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
              >
                {`${fc.label}: ${formattedMinFilterValue}${unit} to ${formattedMaxFilterValue}${unit} `}
                <XIcon
                  onClick={() => {
                    updateFilters({
                      ...filters,
                      [min]: "",
                      [max]: "",
                    });
                  }}
                  className="size-3"
                />
              </Badge>
            );
          }
          return null;
        }
        if (fc.type === "TOGGLE") {
          const { filterKey } = fc;
          const filterValue = filters[filterKey];
          if (!filterValue) return null;
          return (
            <Badge
              key={fc.id}
              variant="outline"
              className="px-2 gap-2 py-1 rounded-md text-xs font-normal"
            >
              {`${fc.label}`}
              <XIcon
                onClick={() => {
                  updateFilters({
                    ...filters,
                    [filterKey]: "",
                  });
                }}
                className="size-3"
              />
            </Badge>
          );
        }
        return null;
      })}
    </div>
  );
}
